
import { useScoreManagement } from "./useScoreManagement";
import { usePointNavigation } from "./usePointNavigation";
import { useTendencyHandlers } from "./useTendencyHandlers";
import { ScoresState, BallTendency } from "./types";
import { getTestPoints } from "@/utils/level3/points";

export const useScoreHandlers = (
  initialScores: ScoresState,
  toast: (props: { title: string; description: string; variant?: "default" | "destructive" }) => void
) => {
  // Split hooks for better organization
  const {
    scores,
    setScores,
    updateScore,
    clearScore,
    resetScores: resetScoresState
  } = useScoreManagement(initialScores);

  const {
    selectedTest,
    setSelectedTest,
    currentPoint,
    setCurrentPoint,
    currentAttempt,
    setCurrentAttempt,
    moveToNextAttempt,
    moveToPreviousAttempt,
    resetNavigation
  } = usePointNavigation(toast);

  const {
    ballTendencies,
    setBallTendencies,
    updateTendency,
    resetTendencies,
    analyzeTendencies
  } = useTendencyHandlers(selectedTest, toast);

  // ---- Combined action handlers ----
  
  const handleScoreChange = (score: number, tendency: BallTendency = null) => {
    console.log(`Recording score ${score} for point ${currentPoint}, attempt ${currentAttempt}, tendency: ${tendency}`);
    
    // Update score
    updateScore(currentPoint, currentAttempt, score);
    
    // Update tendency if needed
    updateTendency(currentPoint, currentAttempt, tendency);
    
    // Debug: Log scores after update
    console.log("Scores after update:", scores);
    
    // Move to next attempt or point
    moveToNextAttempt();
  };
  
  const cancelLastEntry = () => {
    if (!selectedTest) return;
    
    console.log("Canceling last entry for test:", selectedTest);
    console.log("Current point:", currentPoint, "Current attempt:", currentAttempt);
    
    // Determine if we can move back
    const canMoveToPrevious = moveToPreviousAttempt();
    
    if (!canMoveToPrevious) {
      toast({
        title: "Impossible d'annuler",
        description: "Il n'y a pas d'entrée précédente à annuler.",
        variant: "destructive",
      });
      return;
    }
    
    // Clear score at previous position
    clearScore(currentPoint, currentAttempt);
    
    // Clear tendency at previous position (if applicable)
    updateTendency(currentPoint, currentAttempt, null);
    
    // Display confirmation
    toast({
      title: "Entrée annulée",
      description: "La dernière saisie a été supprimée.",
    });
  };
  
  const handleFinish = () => {
    // Analyze ball tendencies if this is a test that tracks them
    const tendencyMessage = analyzeTendencies();
    
    if (tendencyMessage) {
      toast({
        title: "Entraînement terminé",
        description: `${tendencyMessage} Les résultats ont été enregistrés.`,
      });
    } else {
      toast({
        title: "Entraînement terminé",
        description: `Les résultats ont été enregistrés.`,
      });
    }
    
    setSelectedTest(null);
    resetScoresState(selectedTest || "");
    setBallTendencies({});
  };
  
  const handleSelectTest = (testId: string, skipReset: boolean = false) => {
    console.log(`Selecting test: ${testId}, skipReset: ${skipReset}`);
    
    if (!skipReset) {
      // Reset navigation
      resetNavigation(testId);
      
      // Reset scores for this test
      resetScoresState(testId);
      
      // Reset tendencies for this test
      const points = getTestPoints(testId);
      resetTendencies(testId, points);
    }
    
    // Set selected test at the end to trigger any dependent effects
    setSelectedTest(testId);
  };
  
  const handleBackToTests = () => {
    console.log("handleBackToTests called, setting selectedTest to null");
    setSelectedTest(null);
    resetScoresState(selectedTest || "");
    setBallTendencies({});
  };
  
  return {
    scores,
    setScores,
    ballTendencies,
    setBallTendencies,
    selectedTest,
    setSelectedTest,
    currentPoint,
    setCurrentPoint,
    currentAttempt,
    setCurrentAttempt,
    handleScoreChange,
    cancelLastEntry,
    handleFinish,
    handleSelectTest,
    handleBackToTests,
    resetScores: () => resetScoresState(selectedTest || "")
  };
};
